import { router } from "@inertiajs/react"
import { useState } from "react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { rate } from "@/lib/money"
import { pluralize } from "@/lib/relative-time"
import type { TemplateDetail } from "@/types/bids"

const shortDate = (iso: string) =>
  new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" })

/** Version history + the publish action. Publishing freezes the draft's
 *  roll-up as the next version; bids keep their pins until re-pinned —
 *  margins update, quoted prices don't move without you. */
export function VersionRail({ template }: { template: TemplateDetail }) {
  const [publishing, setPublishing] = useState(false)

  const publish = () => {
    setPublishing(true)
    router.post(template.urls.publish, {}, {
      preserveScroll: true,
      onFinish: () => setPublishing(false),
    })
  }

  return (
    <aside className="space-y-4" data-testid="version-rail">
      <Button
        className="w-full"
        disabled={!template.dirty || publishing}
        onClick={publish}
        data-testid="publish-template"
      >
        Save as v{template.next_version}
      </Button>
      {!template.dirty && (
        <p className="text-xs text-ink-muted">
          {template.current_version
            ? `No changes since v${template.current_version}.`
            : "Add lines to publish the first version."}
        </p>
      )}
      {template.dirty && template.current_version && (
        <p className="text-xs text-ink-muted" data-testid="template-draft-badge">
          Draft has unpublished changes — bids keep using v{template.current_version} until
          published and re-pinned.
        </p>
      )}

      <section>
        <h2>Version history</h2>
        <ul className="mt-2 divide-y divide-hairline rounded-lg border border-hairline bg-surface">
          {template.versions.length === 0 && (
            <li className="p-3 text-sm text-ink-muted">Nothing published yet.</li>
          )}
          {template.versions.map((version, index) => (
            <li key={version.id} className="p-3 text-sm" data-testid={`version-${version.version}`}>
              <p className="flex items-center gap-2">
                <strong>v{version.version}</strong>
                {index === 0 && <Badge tone="accent">current</Badge>}
                <span className="ml-auto text-ink-muted">{shortDate(version.published_at)}</span>
              </p>
              <p className="mt-1 text-xs text-ink-muted">
                {rate(version.adder_per_sf, "SF")} · sample{" "}
                {version.sample_label || `${version.sample_sf} SF`} · used by{" "}
                {pluralize(version.used_by_count, "community", "communities")} · {version.created_by}
              </p>
            </li>
          ))}
        </ul>
      </section>
    </aside>
  )
}
